/**
 * Poblaciones de un store desde el **main API** (para las tablas de permisos del canal):
 * operadores (usuarios con permiso en el store) y sellers asociados. Reusa `axiosInstance`
 * (lleva el access_token vía el interceptor del dataProvider) y el proxy `/api/v1`.
 *
 * Endpoints existentes (no se crearon nuevos):
 *   GET /permissions/store/{storeCode}/users      → { data: [{ user: {userId, firstname,...} }] }
 *   GET /store-linked-seller/{storeCode}/sellers   → { data: [{ sellerCode, name }] }
 * Ambos son nestjs-paginate; traemos todas las páginas (cap defensivo).
 */
import { axiosInstance } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";

export interface StoreOperator {
  userId: number;
  name: string;
  email?: string;
}

export interface StoreSeller {
  sellerCode: string;
  name: string;
}

async function fetchAllPaginated<T>(path: string, extraParams: Record<string, unknown> = {}): Promise<T[]> {
  const out: T[] = [];
  let page = 1;
  const limit = 100;
  // Cap defensivo: 20 páginas (2000 ítems) para no loopear infinito.
  for (; page <= 20; page++) {
    const { data } = await axiosInstance.get(`${API_BASE_URL}${path}`, {
      params: { page, limit, ...extraParams },
    });
    const rows: T[] = data?.data ?? [];
    out.push(...rows);
    const totalPages = data?.meta?.totalPages ?? 1;
    if (page >= totalPages || rows.length === 0) break;
  }
  return out;
}

export const storePopulationsApi = {
  listOperators: async (storeCode: string): Promise<StoreOperator[]> => {
    const rows = await fetchAllPaginated<{ user: any }>(`/permissions/store/${storeCode}/users`);
    return rows
      .filter((r) => r?.user)
      .map((r) => ({
        userId: r.user.userId,
        // Un apellido cargado como "-" no es un apellido: se mostraba "andresvalsesia -" (xcons-docs#508).
        name:
          [r.user.firstname, r.user.lastname]
            .map((p) => String(p ?? "").trim())
            .filter((p) => p && !/^-+$/.test(p))
            .join(" ") || `user:${r.user.userId}`,
        email: r.user.email,
      }));
  },

  /** Solo sellers con **link activo** en el store (filtro server-side por storeLinkedSellers.active). */
  listSellers: async (storeCode: string): Promise<StoreSeller[]> => {
    const rows = await fetchAllPaginated<{ sellerCode: string; name: string }>(
      `/store-linked-seller/${storeCode}/sellers`,
      { "filter.storeLinkedSellers.active": "$eq:1" },
    );
    return rows.map((r) => ({ sellerCode: r.sellerCode, name: r.name ?? r.sellerCode }));
  },
};
